import {inject, Injectable} from "@angular/core";
import {HttpClient} from "@angular/common/http";
import {SearchRequest} from "../dto/search/search-table.dto.req";
import {Observable, tap} from "rxjs";
import {SearchContactRes} from "../dto/search/search-contact.res";
import {UrlBuilder} from "../item/url.item";
import {map} from "rxjs/operators";
import {WrapperDto} from "../dto/wrapper.dto";
import {OrgDto} from "../dto/org.dto";
import {ContactDto} from "../dto/contact.dto";
import {VoidDto} from "../dto/void.dto";
import {Additionals} from "../item/additionals";
import {AddressDto} from "../dto/address.dto";
import {StorageService} from "../../services/storage/storage.service";
import {PhoneDto} from "../dto/phone.dto";
import {BankAddressDto} from "../dto/bank-address.dto";
import {EmailDto} from "../dto/email.dto";
import {OccurrenceDto} from "../dto/occurrence.dto";
import {HyperlinkDto} from "../dto/hyperlink.dto";
import {MediaDto} from "../dto/media.dto";
import {UrlDto} from "../item/url.dto";

@Injectable({providedIn: 'root'})
export class ContactRepo {

  private readonly contactSearch: string = '/v1/org/{orgId}/contact/search';
  private readonly contactRead: string = '/v1/org/{orgId}/contact/{id}';
  private readonly contactWrite: string = '/v1/org/{orgId}/contact';
  private readonly contactDelete: string = '/v1/org/{orgId}/contact/{id}/delete';
  private readonly contactPrintoutWrite: string = '/v1/org/{orgId}/contact/{id}/printout';
  private readonly addressAllRead: string = '/v1/org/{orgId}/contact/{id}/address';
  private readonly addressWrite: string = '/v1/org/{orgId}/contact/{id}/address';
  private readonly addressDelete: string = '/v1/org/{orgId}/contact/{id}/address/{addressId}/delete';
  private readonly phoneAllRead: string = '/v1/org/{orgId}/contact/{id}/phone';
  private readonly phoneWrite: string = '/v1/org/{orgId}/contact/{id}/phone';
  private readonly phoneDelete: string = '/v1/org/{orgId}/contact/{id}/phone/{phoneId}/delete';
  private readonly bankAddressAllRead: string = '/v1/org/{orgId}/contact/{id}/bankaddress';
  private readonly bankAddressWrite: string = '/v1/org/{orgId}/contact/{id}/bankaddress';
  private readonly bankAddressDelete: string = '/v1/org/{orgId}/contact/{id}/bankaddress/{bankaddressId}/delete';
  private readonly emailAllRead: string = '/v1/org/{orgId}/contact/{id}/email';
  private readonly emailWrite: string = '/v1/org/{orgId}/contact/{id}/email';
  private readonly emailDelete: string = '/v1/org/{orgId}/contact/{id}/email/{emailId}/delete';
  private readonly occurrenceAllRead: string = '/v1/org/{orgId}/contact/{id}/occurrence';
  private readonly occurrenceWrite: string = '/v1/org/{orgId}/contact/{id}/occurrence';
  private readonly occurrenceDelete: string = '/v1/org/{orgId}/contact/{id}/occurrence/{occurrenceId}/delete';
  private readonly hyperlinkAllRead: string = '/v1/org/{orgId}/contact/{id}/hyperlink';
  private readonly hyperlinkWrite: string = '/v1/org/{orgId}/contact/{id}/hyperlink';
  private readonly hyperlinkDelete: string = '/v1/org/{orgId}/contact/{id}/hyperlink/{hyperlinkId}/delete';

  private readonly http: HttpClient = inject(HttpClient);
  private readonly storage: StorageService = inject(StorageService);

  private readonly contactStoragePrefix: string = 'contact:';
  private readonly contactStorageExpireInSec: number = 600; // 600 = 10 minutes

  public getByIdCached(org: OrgDto, contactId: string): Observable<WrapperDto<ContactDto>> {
    return this.storage.cache(this.buildCacheKey(org, contactId), () => {
      const url: UrlDto = UrlBuilder.builder().addPath(this.contactRead).addAdditionals(Additionals.ALL)
        .addParam('orgId', org.id).addParam('id', contactId).build();
      return this.http.get<WrapperDto<ContactDto>>(url.getUrl(), url.getOptions());
    }, this.contactStorageExpireInSec)
      .pipe(map((res) => new WrapperDto(res)));
  }

  public getById(org: OrgDto, contactId: string, add?: Additionals): Observable<WrapperDto<ContactDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.contactRead).addAdditionals(add)
      .addParam('orgId', org.id).addParam('id', contactId).build();
    return this.http.get<WrapperDto<ContactDto>>(url.getUrl(), url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public update(org: OrgDto, contact: ContactDto): Observable<WrapperDto<ContactDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.contactWrite).addParam('orgId', org.id).build();
    return this.http.post<WrapperDto<ContactDto>>(url.getUrl(), contact, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public delete(org: OrgDto, contactId: string): Observable<WrapperDto<VoidDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.contactDelete)
      .addParam('orgId', org.id).addParam('id', contactId).build();
    return this.http.post<WrapperDto<VoidDto>>(url.getUrl(), {}, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public createPrintout(org: OrgDto, contactId: string): Observable<WrapperDto<MediaDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.contactPrintoutWrite).addParam('orgId', org.id)
      .addParam('id', contactId).build();
    return this.http.post<WrapperDto<MediaDto>>(url.getUrl(), {}, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public cacheRemove(org: OrgDto, contactId: string): void {
    this.storage.cacheRemove(this.buildCacheKey(org, contactId));
  }

  private buildCacheKey(org: OrgDto, contactId: string): string {
    return this.contactStoragePrefix + org.id + ':' + contactId;
  }

  // ##########################################################################
  // #### Search
  // ##########################################################################

  public search(org: OrgDto, request: SearchRequest): Observable<WrapperDto<SearchContactRes>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.contactSearch).addParam('orgId', org.id).build();
    return this.http.post<WrapperDto<SearchContactRes>>(url.getUrl(), request, url.getOptions()).pipe(
      tap((res) => res.dataType = res.dataType === 'SearchResultDto' ? 'SearchContactRes' : res.dataType),
      map((res) => new WrapperDto(res))
    );
  }

  // ##########################################################################
  // #### Address
  // ##########################################################################

  public readAddresses(org: OrgDto, contactId: string): Observable<WrapperDto<AddressDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.addressAllRead)
      .addParam('orgId', org.id).addParam('id', contactId).build();
    return this.http.get<WrapperDto<AddressDto>>(url.getUrl(), url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public updateAddress(org: OrgDto, contactId: string, address: AddressDto): Observable<WrapperDto<AddressDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.addressWrite)
      .addParam('orgId', org.id).addParam('id', contactId).build();
    return this.http.post<WrapperDto<AddressDto>>(url.getUrl(), address, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public deleteAddress(org: OrgDto, contactId: string, addressId: string): Observable<WrapperDto<VoidDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.addressDelete)
      .addParam('orgId', org.id).addParam('id', contactId).addParam('addressId', addressId).build();
    return this.http.post<WrapperDto<VoidDto>>(url.getUrl(), {}, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  // ##########################################################################
  // #### Phone
  // ##########################################################################

  public updatePhone(org: OrgDto, contactId: string, phone: PhoneDto): Observable<WrapperDto<PhoneDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.phoneWrite)
      .addParam('orgId', org.id).addParam('id', contactId).build();
    return this.http.post<WrapperDto<PhoneDto>>(url.getUrl(), phone, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public readPhones(org: OrgDto, contactId: string): Observable<WrapperDto<PhoneDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.phoneAllRead)
      .addParam('orgId', org.id).addParam('id', contactId).build();
    return this.http.get<WrapperDto<PhoneDto>>(url.getUrl(), url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public deletePhone(org: OrgDto, contactId: string, phoneId: string): Observable<WrapperDto<VoidDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.phoneDelete)
      .addParam('orgId', org.id).addParam('id', contactId).addParam('phoneId', phoneId).build();
    return this.http.post<WrapperDto<VoidDto>>(url.getUrl(), {}, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  // ##########################################################################
  // #### BankAddress
  // ##########################################################################

  public readBankAddresses(org: OrgDto, contactId: string): Observable<WrapperDto<BankAddressDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.bankAddressAllRead)
      .addParam('orgId', org.id).addParam('id', contactId).build();
    return this.http.get<WrapperDto<BankAddressDto>>(url.getUrl(), url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public updateBankAddress(org: OrgDto, contactId: string, bankAddress: BankAddressDto): Observable<WrapperDto<BankAddressDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.bankAddressWrite)
      .addParam('orgId', org.id).addParam('id', contactId).build();
    return this.http.post<WrapperDto<BankAddressDto>>(url.getUrl(), bankAddress, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public deleteBankAddress(org: OrgDto, contactId: string, bankaddressId: string): Observable<WrapperDto<VoidDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.bankAddressDelete)
      .addParam('orgId', org.id).addParam('id', contactId).addParam('bankaddressId', bankaddressId).build();
    return this.http.post<WrapperDto<VoidDto>>(url.getUrl(), {}, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  // ##########################################################################
  // #### Email
  // ##########################################################################

  public getEmails(org: OrgDto, contactId: string): Observable<WrapperDto<EmailDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.emailAllRead)
      .addParam('orgId', org.id).addParam('id', contactId).build();
    return this.http.get<WrapperDto<EmailDto>>(url.getUrl(), url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public updateEmail(org: OrgDto, contactId: string, email: EmailDto): Observable<WrapperDto<EmailDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.emailWrite)
      .addParam('orgId', org.id).addParam('id', contactId).build();
    return this.http.post<WrapperDto<EmailDto>>(url.getUrl(), email, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public deleteEmail(org: OrgDto, contactId: string, emailId: string): Observable<WrapperDto<VoidDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.emailDelete)
      .addParam('orgId', org.id).addParam('id', contactId).addParam('emailId', emailId).build();
    return this.http.post<WrapperDto<VoidDto>>(url.getUrl(), {}, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  // ##########################################################################
  // #### Occurrence
  // ##########################################################################

  public getOccurrences(org: OrgDto, contactId: string): Observable<WrapperDto<OccurrenceDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.occurrenceAllRead)
      .addParam('orgId', org.id).addParam('id', contactId).build();
    return this.http.get<WrapperDto<OccurrenceDto>>(url.getUrl(), url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public updateOccurrence(org: OrgDto, contactId: string, occurrence: OccurrenceDto): Observable<WrapperDto<OccurrenceDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.occurrenceWrite)
      .addParam('orgId', org.id).addParam('id', contactId).build();
    return this.http.post<WrapperDto<OccurrenceDto>>(url.getUrl(), occurrence, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public deleteOccurrence(org: OrgDto, contactId: string, occurrenceId: string): Observable<WrapperDto<VoidDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.occurrenceDelete)
      .addParam('orgId', org.id).addParam('id', contactId).addParam('occurrenceId', occurrenceId).build();
    return this.http.post<WrapperDto<VoidDto>>(url.getUrl(), {}, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  // ##########################################################################
  // #### Hyperlink
  // ##########################################################################

  public getHyperlinks(org: OrgDto, contactId: string): Observable<WrapperDto<HyperlinkDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.hyperlinkAllRead)
      .addParam('orgId', org.id).addParam('id', contactId).build();
    return this.http.get<WrapperDto<HyperlinkDto>>(url.getUrl(), url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public updateHyperlink(org: OrgDto, contactId: string, hyperlink: HyperlinkDto): Observable<WrapperDto<HyperlinkDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.hyperlinkWrite)
      .addParam('orgId', org.id).addParam('id', contactId).build();
    return this.http.post<WrapperDto<HyperlinkDto>>(url.getUrl(), hyperlink, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }

  public deleteHyperlink(org: OrgDto, contactId: string, hyperlinkId: string): Observable<WrapperDto<VoidDto>> {
    const url: UrlDto = UrlBuilder.builder().addPath(this.hyperlinkDelete)
      .addParam('orgId', org.id).addParam('id', contactId).addParam('hyperlinkId', hyperlinkId).build();
    return this.http.post<WrapperDto<VoidDto>>(url.getUrl(), {}, url.getOptions())
      .pipe(map((res) => new WrapperDto(res)));
  }
}
